import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import { handleError } from '@/utils/handleError';
import {
  BookkeepingSuggestion,
  BookkeepingSuggestionStatus,
  VerificationType,
  CreateSuggestionResponse,
  ListSuggestionsResponse,
  UpdateSuggestionStatusResponse,
  DeclineAndRetryResponse,
} from "@/types/bookkeepingSuggestion";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export interface ListSuggestionsFilters {
  verificationType?: VerificationType | 'all';
  dateFrom?: string;
  dateTo?: string;
}

export interface PaginationParams {
  limit: number;
  offset: number;
}

export class BookkeepingSuggestionApiClient {
  /**
   * Create a bookkeeping suggestion (queues a background job)
   */
  async createSuggestion(
    verificationType: VerificationType,
    verificationId: string
  ): Promise<CreateSuggestionResponse> {
    try {
      console.log(`BookkeepingSuggestionApiClient: Creating suggestion for ${verificationType}: ${verificationId}`);
      const { data, error } = await supabase.functions.invoke('bookkeeping-suggestion-api', {
        body: {
          action: 'createSuggestion',
          verificationType,
          verificationId
        }
      });

      if (error) throw error;

      console.log('BookkeepingSuggestionApiClient: Suggestion job queued:', data);
      return data as CreateSuggestionResponse;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToCreateBookkeepingSuggestion'), 'createSuggestion');
      throw error;
    }
  }

  /**
   * List pending bookkeeping suggestions
   */
  async listPendingSuggestions(
    filters?: ListSuggestionsFilters,
    pagination?: PaginationParams
  ): Promise<ListSuggestionsResponse> {
    try {
      console.log('BookkeepingSuggestionApiClient: Listing pending suggestions');
      const { data, error } = await supabase.functions.invoke('bookkeeping-suggestion-api', {
        body: {
          action: 'listPendingSuggestions',
          filters: filters || {},
          pagination: pagination || { limit: 50, offset: 0 }
        }
      });

      if (error) throw error;

      console.log(`BookkeepingSuggestionApiClient: Received ${data.suggestions?.length || 0} suggestions`);
      return data as ListSuggestionsResponse;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToListBookkeepingSuggestions'), 'listPendingSuggestions');
      throw error;
    }
  }

  /**
   * Get a specific bookkeeping suggestion by ID
   */
  async getSuggestion(id: string): Promise<BookkeepingSuggestion | null> {
    try {
      console.log(`BookkeepingSuggestionApiClient: Getting suggestion ${id}`);
      const { data, error } = await supabase.functions.invoke('bookkeeping-suggestion-api', {
        body: {
          action: 'getSuggestion',
          id
        }
      });

      if (error) throw error;

      return data.suggestion || null;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToGetBookkeepingSuggestion'), 'getSuggestion');
      throw error;
    }
  }

  /**
   * Update bookkeeping suggestion status (approve or reject)
   */
  async updateSuggestionStatus(
    id: string,
    status: BookkeepingSuggestionStatus,
    selectedSuggestionId?: string,
    rejectionReason?: string,
    approvalReason?: string
  ): Promise<UpdateSuggestionStatusResponse> {
    console.log(`BookkeepingSuggestionApiClient: Updating suggestion ${id} to ${status}`);
    const { data, error } = await supabase.functions.invoke('bookkeeping-suggestion-api', {
      body: {
        action: 'updateSuggestionStatus',
        id,
        status,
        selectedSuggestionId,
        rejectionReason,
        approvalReason
      }
    });

    // When edge function returns non-2xx, Supabase puts error details in data
    if (data && typeof data === 'object' && 'error' in data) {
      const errorMessage = data.message || data.error || 'Unknown error';
      throw new Error(errorMessage);
    }

    if (error) throw await toEdgeFunctionFailure(error, 'Failed to update bookkeeping suggestion');

    console.log(`BookkeepingSuggestionApiClient: Suggestion ${id} updated successfully`);
    return data as UpdateSuggestionStatusResponse;
  }

  /**
   * Decline a suggestion and queue a retry with user context
   */
  async declineAndRetry(
    id: string,
    declineContext: string
  ): Promise<DeclineAndRetryResponse> {
    console.log(`BookkeepingSuggestionApiClient: Declining and retrying suggestion ${id}`);
    const { data, error } = await supabase.functions.invoke('bookkeeping-suggestion-api', {
      body: {
        action: 'declineAndRetry',
        id,
        declineContext
      }
    });

    // Handle error responses
    if (data && typeof data === 'object' && 'error' in data) {
      const errorMessage = data.message || data.error || 'Unknown error';
      throw new Error(errorMessage);
    }

    if (error) throw await toEdgeFunctionFailure(error, 'Failed to decline suggestion');

    console.log(`BookkeepingSuggestionApiClient: Suggestion ${id} declined, retry queued: ${data.retryJob.jobId}`);
    return data as DeclineAndRetryResponse;
  }

  /**
   * Mark suggestion for manual review (when max retries reached)
   */
  async markForManualReview(
    id: string,
    reason: string
  ): Promise<BookkeepingSuggestion> {
    console.log(`BookkeepingSuggestionApiClient: Marking suggestion ${id} for manual review`);
    const { data, error } = await supabase.functions.invoke('bookkeeping-suggestion-api', {
      body: {
        action: 'markForManualReview',
        id,
        reason
      }
    });

    // Handle error responses
    if (data && typeof data === 'object' && 'error' in data) {
      const errorMessage = data.message || data.error || 'Unknown error';
      throw new Error(errorMessage);
    }

    if (error) throw await toEdgeFunctionFailure(error, 'Failed to mark for manual review');

    console.log(`BookkeepingSuggestionApiClient: Suggestion ${id} marked for manual review`);
    return data.suggestion as BookkeepingSuggestion;
  }
}
